import { Datakatalog } from '@/bootstrap/data';
import {
    AdvancedFilterGroupNode,
    AdvancedFilterNode,
    compileAdvancedFilter,
    createAdvancedFilterEgenskapNode,
    createAdvancedFilterGroup,
    createAdvancedFilterOverlappNode,
    createAdvancedFilterRelasjonNode,
    validateAdvancedFilter,
} from '@/domain/advancedFilter';
import { EgenskapTillattVerdi, Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { OperatorE } from '@/middleware/queries/Operator';
import { stateUser } from '@/selectors/selectors';
import { VegobjekttypeState } from '@/state';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { useSelector } from '@/state/store';
import { ColorIndex, resolveColor } from '@/utils/ColorHelper';
import * as React from 'react';
import { AdvancedFilterGroupEditor } from './AdvancedFilterGroupEditor';
import { AdvancedFilterRawPreview } from './AdvancedFilterRawPreview';

type FilterVisualKind = 'egenskap' | 'overlapp' | 'relasjon' | 'group';

interface TutorialExample {
    title: string;
    story: string;
    vegobjekttype: Vegobjekttype;
    root: AdvancedFilterGroupNode;
}

type WhatListElementStyle = React.CSSProperties & {
    '--c-what-list-element-extra-depth'?: string;
    '--right-panel-card-min-width'?: string;
};

const FILTER_VISUALS: Record<
    FilterVisualKind,
    { icon: string; label: string; modifier: string; heading: string }
> = {
    egenskap: {
        icon: 'filter_list',
        label: 'Egenskap',
        modifier: 'egenskap',
        heading: 'Egenskapsfiltere',
    },
    overlapp: {
        icon: 'join_inner',
        label: 'Overlapp',
        modifier: 'overlapp',
        heading: 'Overlapsfiltere',
    },
    relasjon: {
        icon: 'compare_arrows',
        label: 'Relasjon',
        modifier: 'relasjon',
        heading: 'Relasjonsfiltere',
    },
    group: {
        icon: 'workspaces',
        label: 'Underfilter',
        modifier: 'group',
        heading: 'Underfiltere',
    },
};

function normalizeText(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function resolveType(
    datakatalog: Datakatalog,
    id: number | null,
    names: string[]
): Vegobjekttype | null {
    if (id !== null) {
        const typeById = datakatalog.getTypeById(id);
        if (typeById) {
            return typeById;
        }
    }

    const types = datakatalog.vegobjekttyper.filter(type => !type.abstrakt_type);

    for (const name of names) {
        const exactMatch = types.find(
            type => normalizeText(type.navn) === normalizeText(name)
        );
        if (exactMatch) {
            return exactMatch;
        }
    }

    for (const name of names) {
        const partialMatch = types.find(type =>
            normalizeText(type.navn).includes(normalizeText(name))
        );
        if (partialMatch) {
            return partialMatch;
        }
    }

    return null;
}

function resolveProperty(
    type: Vegobjekttype,
    id: number,
    names: string[] = []
): Egenskapstype | null {
    const propertyById = type.egenskapstypeById(id);
    if (propertyById) {
        return propertyById;
    }

    for (const name of names) {
        const exactMatch = type.egenskapstyper.find(
            property => normalizeText(property.navn) === normalizeText(name)
        );
        if (exactMatch) {
            return exactMatch;
        }
    }

    for (const name of names) {
        const partialMatch = type.egenskapstyper.find(property =>
            normalizeText(property.navn).includes(normalizeText(name))
        );
        if (partialMatch) {
            return partialMatch;
        }
    }

    return null;
}

function resolveEnumValue(
    property: Egenskapstype,
    id: number,
    names: string[] = []
): EgenskapTillattVerdi | null {
    const valueById = property.verdiById(id);
    if (valueById) {
        return valueById;
    }

    for (const name of names) {
        const exactMatch = property.tillatte_verdier.find(
            value =>
                normalizeText(String(value.verdi)) === normalizeText(name) ||
                normalizeText(value.kortnavn) === normalizeText(name)
        );
        if (exactMatch) {
            return exactMatch;
        }
    }

    for (const name of names) {
        const partialMatch = property.tillatte_verdier.find(
            value =>
                normalizeText(String(value.verdi)).includes(normalizeText(name)) ||
                normalizeText(value.kortnavn).includes(normalizeText(name))
        );
        if (partialMatch) {
            return partialMatch;
        }
    }

    return null;
}

function getAdvancedFilterExtraDepth(
    group: AdvancedFilterGroupNode | null,
    depth = 0
): number {
    if (!group) {
        return 0;
    }

    return group.children.reduce((maxDepth, child) => {
        return Math.max(maxDepth, getAdvancedFilterNodeExtraDepth(child, depth));
    }, depth);
}

function getAdvancedFilterNodeExtraDepth(
    node: AdvancedFilterNode,
    depth: number
): number {
    if (node.kind === 'group') {
        return getAdvancedFilterExtraDepth(node, depth + 1);
    }

    if ((node.kind === 'overlapp' || node.kind === 'relasjon') && node.child) {
        return getAdvancedFilterExtraDepth(node.child, depth + 1);
    }

    return depth;
}

function buildBridgeExample(datakatalog: Datakatalog): TutorialExample | null {
    const bru = resolveType(datakatalog, null, ['Bru']);
    const byggeaar = bru ? resolveProperty(bru, 10278, ['Byggeår']) : null;
    const lengde = bru ? resolveProperty(bru, 1313, ['Lengde']) : null;

    if (!bru || !byggeaar || !lengde) {
        return null;
    }

    return {
        title: 'Finn nyere, korte bruer',
        story: 'Jeg vil finne bruer som er bygget fra år 2000 og som er 100 meter lange eller kortere.',
        vegobjekttype: bru,
        root: createAdvancedFilterGroup('AND', [
            createAdvancedFilterEgenskapNode(byggeaar, OperatorE.BEQ, 2000),
            createAdvancedFilterEgenskapNode(lengde, OperatorE.LEQ, 100),
        ]),
    };
}

function buildTrafficAccidentOverlapExample(
    datakatalog: Datakatalog
): TutorialExample | null {
    const trafikkulykke = resolveType(datakatalog, null, ['Trafikkulykke']);
    const fartsgrense = resolveType(datakatalog, 105, ['Fartsgrense']);
    const fartsgrenseProperty = fartsgrense
        ? resolveProperty(fartsgrense, 2021, ['Fartsgrense'])
        : null;
    const value80 = fartsgrenseProperty
        ? resolveEnumValue(fartsgrenseProperty, 2738, ['80', '80 km/t'])
        : null;

    if (!trafikkulykke || !fartsgrense || !fartsgrenseProperty || !value80) {
        return null;
    }

    return {
        title: 'Finn trafikkulykker der fartsgrensen er 80',
        story: 'Jeg vil finne trafikkulykker som overlapper med en strekning der fartsgrensen er 80 km/t.',
        vegobjekttype: trafikkulykke,
        root: createAdvancedFilterGroup('AND', [
            createAdvancedFilterOverlappNode(
                fartsgrense.id,
                createAdvancedFilterGroup('AND', [
                    createAdvancedFilterEgenskapNode(
                        fartsgrenseProperty,
                        OperatorE.EQ,
                        value80
                    ),
                ])
            ),
        ]),
    };
}

function buildTunnelRelationExample(datakatalog: Datakatalog): TutorialExample | null {
    const tunnel = resolveType(datakatalog, null, ['Tunnel']);
    const tunnellop = resolveType(datakatalog, 67, ['Tunnelløp', 'Tunnellop']);
    const lengde = tunnellop ? resolveProperty(tunnellop, 1317, ['Lengde']) : null;

    if (!tunnel || !tunnellop || !lengde) {
        return null;
    }

    return {
        title: 'Finn tunneler med lange tunnelløp',
        story: 'Jeg vil finne tunneler som har relasjon til et tunnelløp som er lengre enn 2 km.',
        vegobjekttype: tunnel,
        root: createAdvancedFilterGroup('AND', [
            createAdvancedFilterRelasjonNode(
                tunnellop.id,
                createAdvancedFilterGroup('AND', [
                    createAdvancedFilterEgenskapNode(lengde, OperatorE.GT, 2000),
                ])
            ),
        ]),
    };
}

function buildWeekendTrafficAccidentExample(
    datakatalog: Datakatalog
): TutorialExample | null {
    const trafikkulykke = resolveType(datakatalog, null, ['Trafikkulykke']);
    const ukedag = trafikkulykke
        ? resolveProperty(trafikkulykke, 5054, ['Ukedag'])
        : null;
    const skadegrad = trafikkulykke
        ? resolveProperty(trafikkulykke, 5074, ['Skadegrad'])
        : null;
    const lordag = ukedag ? resolveEnumValue(ukedag, 6248, ['Lørdag', 'Lordag']) : null;
    const sondag = ukedag ? resolveEnumValue(ukedag, 6249, ['Søndag', 'Sondag']) : null;
    const alvorligSkadd = skadegrad
        ? resolveEnumValue(skadegrad, 6429, ['Alvorlig skadd'])
        : null;

    if (!trafikkulykke || !ukedag || !skadegrad || !lordag || !sondag || !alvorligSkadd) {
        return null;
    }

    return {
        title: 'Finn helgeulykker med alvorlig skadegrad',
        story: 'Jeg vil finne trafikkulykker som skjedde på lørdag eller søndag, og som samtidig har skadegrad alvorlig skadd.',
        vegobjekttype: trafikkulykke,
        root: createAdvancedFilterGroup('AND', [
            createAdvancedFilterGroup('OR', [
                createAdvancedFilterEgenskapNode(ukedag, OperatorE.EQ, lordag),
                createAdvancedFilterEgenskapNode(ukedag, OperatorE.EQ, sondag),
            ]),
            createAdvancedFilterEgenskapNode(skadegrad, OperatorE.EQ, alvorligSkadd),
        ]),
    };
}

function buildControlStationWithoutWeightRelationExample(
    datakatalog: Datakatalog
): TutorialExample | null {
    const kontrollVeieplass = resolveType(datakatalog, 44, [
        'Kontroll-/veieplass',
        'Kontroll-/veiplass',
    ]);
    const veiing = kontrollVeieplass
        ? resolveProperty(kontrollVeieplass, 1117, ['Veiing'])
        : null;
    const fastmontertVekt = veiing
        ? resolveEnumValue(veiing, 3923, ['Fastmontert vekt'])
        : null;
    const vekt = resolveType(datakatalog, 155, ['Vekt']);

    if (!kontrollVeieplass || !veiing || !fastmontertVekt || !vekt) {
        return null;
    }

    return {
        title: 'Finn kontroll-/veieplasser uten relasjon til vekt',
        story: 'Jeg vil finne Kontroll-/veieplass der Veiing er Fastmontert vekt, men som ikke har relasjon til Vekt.',
        vegobjekttype: kontrollVeieplass,
        root: createAdvancedFilterGroup('AND', [
            createAdvancedFilterEgenskapNode(veiing, OperatorE.EQ, fastmontertVekt),
            createAdvancedFilterRelasjonNode(vekt.id, null, true),
        ]),
    };
}

function buildTutorialExamples(datakatalog: Datakatalog): TutorialExample[] {
    return [
        buildBridgeExample(datakatalog),
        buildTrafficAccidentOverlapExample(datakatalog),
        buildTunnelRelationExample(datakatalog),
        buildWeekendTrafficAccidentExample(datakatalog),
        buildControlStationWithoutWeightRelationExample(datakatalog),
    ].filter((example): example is TutorialExample => example !== null);
}

const FilterTypeInline: React.FC<{ kind: FilterVisualKind; className?: string }> = ({
    kind,
    className = '',
}) => {
    const visual = FILTER_VISUALS[kind];

    return (
        <span
            className={`c-advanced-filter-tutorial__filter-token c-advanced-filter-tutorial__filter-token--${visual.modifier} ${className}`.trim()}
        >
            <i
                className="material-icons c-advanced-filter__node-kind-icon"
                aria-hidden="true"
            >
                {visual.icon}
            </i>
            <span>{visual.label}</span>
        </span>
    );
};

const SectionTitle: React.FC<{ kind?: FilterVisualKind; children?: React.ReactNode }> = ({
    kind,
    children,
}) => (
    <p className="c-advanced-filter-tutorial__section-title">
        <span className="c-advanced-filter-tutorial__section-title-content">
            {kind ? (
                <FilterTypeInline
                    kind={kind}
                    className="c-advanced-filter-tutorial__heading-token"
                />
            ) : null}
            {children ? <span>{children}</span> : null}
        </span>
    </p>
);

const OperatorTitle: React.FC<{
    selected: 'AND' | 'OR';
    onSelect: (operator: 'AND' | 'OR') => void;
}> = ({ selected, onSelect }) => (
    <p className="c-advanced-filter-tutorial__section-title">
        <span className="c-advanced-filter-tutorial__section-title-content">
            <span>Logiske operatorer:</span>
            <ConnectorPreview selected={selected} interactive onSelect={onSelect} />
        </span>
    </p>
);

const FilterTogglePreview: React.FC<{ icon: string; label: string }> = ({
    icon,
    label,
}) => (
    <span className="c-advanced-filter-tutorial__toggle-preview c-advanced-filter__toggle-button">
        <i className="material-icons c-advanced-filter__toggle-icon" aria-hidden="true">
            {icon}
        </i>
        <span className="c-advanced-filter__toggle-label">{label}</span>
    </span>
);

const ConnectorPreview: React.FC<{
    selected: 'AND' | 'OR';
    interactive?: boolean;
    onSelect?: (operator: 'AND' | 'OR') => void;
}> = ({ selected, interactive = false, onSelect }) => (
    <span
        className="c-advanced-filter__connector c-advanced-filter-tutorial__connector-preview"
        role={interactive ? 'radiogroup' : undefined}
        aria-hidden={interactive ? undefined : 'true'}
        aria-label={interactive ? 'Velg logisk operator' : undefined}
    >
        {interactive ? (
            <button
                type="button"
                role="radio"
                aria-checked={selected === 'AND'}
                className={`c-advanced-filter__connector-option${
                    selected === 'AND'
                        ? ' c-advanced-filter__connector-option--selected'
                        : ''
                }`}
                onClick={() => onSelect?.('AND')}
            >
                og
            </button>
        ) : (
            <span
                className={`c-advanced-filter__connector-option${
                    selected === 'AND'
                        ? ' c-advanced-filter__connector-option--selected'
                        : ''
                }`}
            >
                og
            </span>
        )}
        {interactive ? (
            <button
                type="button"
                role="radio"
                aria-checked={selected === 'OR'}
                className={`c-advanced-filter__connector-option${
                    selected === 'OR'
                        ? ' c-advanced-filter__connector-option--selected'
                        : ''
                }`}
                onClick={() => onSelect?.('OR')}
            >
                eller
            </button>
        ) : (
            <span
                className={`c-advanced-filter__connector-option${
                    selected === 'OR'
                        ? ' c-advanced-filter__connector-option--selected'
                        : ''
                }`}
            >
                eller
            </span>
        )}
    </span>
);

const DatakatalogButtonPreview: React.FC = () => (
    <span className="c-advanced-filter-tutorial__datakatalog-trigger">
        <span
            className="c-search-interact__datakatalog-button o-icon-book c-advanced-filter-tutorial__datakatalog-button"
            aria-hidden="true"
        />
        <span>-knappen</span>
    </span>
);

const VisIVegkartPreview: React.FC = () => (
    <span className="c-advanced-filter-tutorial__map-link">
        <i
            className="material-icons c-advanced-filter-tutorial__map-link-icon"
            aria-hidden="true"
        >
            map
        </i>
        <span>Vis i Vegkart</span>
    </span>
);

const TutorialExampleCard: React.FC<{
    example: TutorialExample;
    index: number;
    allTypes: Vegobjekttype[];
}> = ({ example, index, allTypes }) => {
    const user = useSelector(stateUser);
    const color = React.useMemo(() => new ColorIndex(index, 0), [index]);
    const vegobjekttypeData = React.useMemo(() => {
        return new VegobjekttypeState()
            .withTypeId(example.vegobjekttype.id)
            .withColor(color)
            .withAdvancedFilters(example.root, example.root)
            .withLoading(false);
    }, [color, example]);
    const validation = React.useMemo(
        () => validateAdvancedFilter(example.root),
        [example.root]
    );
    const elementStyle: WhatListElementStyle = React.useMemo(
        () => ({
            '--c-what-list-element-extra-depth': getAdvancedFilterExtraDepth(
                example.root
            ).toString(),
            '--right-panel-card-min-width': 'min(100%, 38rem)',
        }),
        [example.root]
    );
    const visibilityIcon = example.vegobjekttype.isLine() ? 'timeline' : 'room';
    const preview = compileAdvancedFilter(example.root);

    return (
        <article className="c-advanced-filter-tutorial__example-card">
            <div className="c-advanced-filter-tutorial__example-copy">
                <p className="c-advanced-filter-tutorial__example-title">
                    {example.title}
                </p>
                <p className="c-advanced-filter-tutorial__example-story">
                    {example.story}
                </p>
            </div>

            <section
                className="c-what c-advanced-filter-tutorial__example-what"
                aria-label={`Eksempelkort for ${example.vegobjekttype.navn}`}
            >
                <ul
                    className="c-what-list o-list c-advanced-filter-tutorial__example-what-list"
                    aria-label={`Eksempel på kort for ${example.vegobjekttype.navn}`}
                >
                    <li
                        className="c-what-list__element c-advanced-filter-tutorial__example-what-element"
                        style={elementStyle}
                    >
                        <div className="c-hva-info">
                            <div className="c-hva-info__leading">
                                <span
                                    className="material-icons c-hva-info__visible"
                                    style={{ color: resolveColor(color) }}
                                    aria-hidden="true"
                                >
                                    {visibilityIcon}
                                </span>
                            </div>
                            <div className="c-hva-info__content">
                                <h3 className="c-hva-info__title">
                                    <span>{example.vegobjekttype.navn}</span>
                                    <span className="c-hva-info__id">
                                        {example.vegobjekttype.id}
                                    </span>
                                </h3>
                                <div className="c-hva-info__actions">
                                    <div className="c-categorize-container">
                                        <button
                                            className="c-hva-info__button"
                                            value="categorize"
                                            id="categorize_button"
                                            type="button"
                                            tabIndex={-1}
                                            aria-hidden="true"
                                        >
                                            <i
                                                className="o-icon-category c-categorize__icon"
                                                aria-hidden="true"
                                            />
                                            <span className="c-advanced-filter__toggle-label">
                                                Kategoriser
                                            </span>
                                        </button>
                                    </div>
                                    <div className="c-advanced-filter__toggle-group">
                                        <button
                                            type="button"
                                            tabIndex={-1}
                                            className="c-hva-info__button c-advanced-filter__toggle-button"
                                            aria-hidden="true"
                                        >
                                            <i
                                                className="material-icons c-advanced-filter__toggle-icon"
                                                aria-hidden="true"
                                            >
                                                clear
                                            </i>
                                            <span className="c-advanced-filter__toggle-label">
                                                Fjern alle filtere
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                tabIndex={-1}
                                className="c-hva-info__remove-button"
                                aria-hidden="true"
                            >
                                <i
                                    className="o-icon-remove c-hva-info__remove-icon"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>

                        <section
                            className="c-advanced-filter c-advanced-filter-tutorial__example-builder"
                            aria-label={`Eksempel på avansert filter: ${example.title}`}
                        >
                            <div className="c-advanced-filter__body">
                                <AdvancedFilterGroupEditor
                                    root={example.root}
                                    group={example.root}
                                    groupDepth={1}
                                    isRoot
                                    egenskapstyper={example.vegobjekttype.egenskapstyper}
                                    vegobjekttypeData={vegobjekttypeData}
                                    datakatalogVegobjekttyper={allTypes}
                                    errorsByNodeId={validation.errorsByNodeId}
                                    onDraftChange={() => {}}
                                    user={user}
                                    disableSuggestedValueFetch
                                />
                            </div>
                            <AdvancedFilterRawPreview
                                draftPreview={preview}
                                appliedPreview={preview}
                                isValid={validation.valid}
                            />
                        </section>
                    </li>
                </ul>
            </section>
        </article>
    );
};

export const AdvancedFilterTutorial: React.FC = () => {
    const [selectedOperator, setSelectedOperator] = React.useState<'AND' | 'OR'>('AND');
    const configAndData = React.useContext(ConfigAndDataContext);
    const datakatalog = configAndData?.data?.datakatalog;

    const allTypes = React.useMemo(() => {
        if (!datakatalog) {
            return [];
        }

        return [...datakatalog.vegobjekttyper]
            .filter(type => !type.abstrakt_type)
            .sort((left, right) => left.navn.localeCompare(right.navn, 'nb'));
    }, [datakatalog]);

    const tutorialExamples = React.useMemo(() => {
        if (!datakatalog) {
            return [];
        }

        return buildTutorialExamples(datakatalog);
    }, [datakatalog]);

    return (
        <div className="c-advanced-filter-tutorial">
            <h3 className="c-advanced-filter-tutorial__title">Avanserte filtere</h3>
            <p className="c-advanced-filter-tutorial__lead">
                Avanserte filtere lar deg beskrive mer presise søk ved å kombinere flere
                betingelser for egenskaper, overlappende stedfesting og relasjoner
                (foreldre/barn) mellom vegobjekter.
            </p>

            <section className="c-advanced-filter-tutorial__section">
                <SectionTitle>Kom i gang</SectionTitle>
                <p>
                    Trykk{' '}
                    <FilterTogglePreview icon="filter_list" label="Legg til filter" /> for
                    å åpne dialogen.
                </p>
                <p>
                    Der kan du legge til filtrering basert på{' '}
                    <FilterTypeInline kind="egenskap" />,
                    <FilterTypeInline kind="overlapp" />,{' '}
                    <FilterTypeInline kind="relasjon" /> og{' '}
                    <FilterTypeInline kind="group" />.
                </p>
                <ul className="c-advanced-filter-tutorial__list">
                    <li>
                        Trykk{' '}
                        <FilterTogglePreview icon="clear" label="Fjern alle filtere" />{' '}
                        for å nullstille alt som er lagt til.
                    </li>
                    <li>
                        Trykk på{' '}
                        <span className="c-advanced-filter-tutorial__remove-preview">
                            Fjern
                        </span>
                        -knappen ved siden av et filter for å slette bare det ene
                        filteret.
                    </li>
                </ul>
            </section>

            <div
                className="c-advanced-filter-tutorial__sections"
                aria-label="Forklaring av filtertypene"
            >
                <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--egenskap">
                    <SectionTitle kind="egenskap" />
                    <p>
                        Egenskapsfiltere lar deg filtrere vegobjekter basert på deres
                        egenskaper. Du kan velge en egenskapstype, en operator som lik,
                        ulik, større enn eller mindre enn, og en verdi som avgrenser
                        søket.
                    </p>
                </section>

                <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--overlapp">
                    <SectionTitle kind="overlapp" />
                    <p>
                        Overlapsfiltere lar deg filtrere på stedfesting. Vegobjekttypen du
                        velger i overlapsfilteret må ha en stedfesting som overlapper helt
                        eller delvis med stedfestingen til vegobjekttypen du søker etter.
                        Hele objektet vises ved delvis overlapp.
                    </p>
                </section>

                <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--relasjon">
                    <SectionTitle kind="relasjon" />
                    <p>
                        Relasjonsfiltere lar deg filtrere vegobjekter basert på relasjoner
                        mellom foreldre og barn. Spørringen viser da vegobjekter med
                        relasjon til den valgte vegobjekttypen.
                    </p>
                    <p>
                        Nedtrekksmenyen foreslår bare gyldige relasjoner for
                        vegobjekttypen du søker etter, basert på datakatalogen.
                    </p>
                </section>
            </div>

            <section className="c-advanced-filter-tutorial__section">
                <OperatorTitle
                    selected={selectedOperator}
                    onSelect={setSelectedOperator}
                />
                <p>
                    Når du har lagt til flere filtere, kan du velge om de skal kombineres
                    med
                    <strong> og</strong> eller <strong>eller</strong>.
                </p>
                <ul className="c-advanced-filter-tutorial__list">
                    <li>
                        <strong>og</strong> henter bare vegobjekter som oppfyller alle
                        kriteriene.
                    </li>
                    <li>
                        <strong>eller</strong> henter vegobjekter som oppfyller minst ett
                        kriterium.
                    </li>
                </ul>
            </section>

            <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--underfilter">
                <SectionTitle kind="group" />
                <p>
                    Underfiltere er måten du grupperer flere filtere sammen på, slik at
                    spørringen tolkes riktig.
                </p>
                <p>
                    De fungerer som parenteser i spørringen og samler flere filtere med én
                    felles logisk operator. Du kan ikke bruke både <strong>og</strong> og
                    <strong> eller</strong> i samme underfilter eller toppfilter, slik at
                    spørringen forblir entydig.
                </p>
                <p>
                    Eksempel:{' '}
                    <strong>(egenskap(A) og egenskap(B)) eller egenskap(C)</strong>{' '}
                    tilsvarer et underfilter med A <strong>og</strong> B, etterfulgt av
                    <strong> eller</strong> utenfor underfilteret mot C.
                </p>
                <p>
                    Du kan også legge til filtere inne i{' '}
                    <FilterTypeInline kind="overlapp" />
                    og <FilterTypeInline kind="relasjon" /> for å beskrive hvilke
                    overlappende eller relaterte objekter som skal telle. Et underfilter
                    vil da automatisk bli lagt til og gruppere disse filterene.
                </p>
            </section>

            <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--soft">
                <SectionTitle>Filtrere uten</SectionTitle>
                <p>
                    Valg av <strong>uten</strong> gjør at man spør om det motsatte av det
                    man spør om i vanlige filtere.
                </p>
                <p>
                    Ved å trykke på <strong>Overlapp</strong>, <strong>Relasjon</strong>{' '}
                    eller <strong>Underfilter</strong>, kan du istedenfor legge til
                    filtere med <strong>Uten overlapp</strong>,{' '}
                    <strong>Uten relasjon</strong> og <strong>Underfilter uten</strong>.
                    Dette lar deg søke etter vegobjekter som ikke har overlappende eller
                    relaterte objekter som oppfyller kriteriene du setter i filteret,
                    eller som ikke oppfyller kriteriene i underfilteret.
                </p>
                <p>
                    Den genererte filterteksten vil da vise `NOT` slik det brukes i Les
                    API.
                </p>
            </section>

            <section className="c-advanced-filter-tutorial__section-group">
                <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--soft">
                    <p className="c-advanced-filter-tutorial__section-title">
                        Generert filter
                    </p>
                    <p>
                        Når du bygger et avansert filter, genereres det automatisk en
                        filtertekst som tilsvarer filteret som brukes i API-spørringen.
                        Filterteksten oppdateres fortløpende når du legger til eller
                        fjerner filtere.
                    </p>
                    <p>
                        For mer informasjon, les dokumentasjonen for{' '}
                        <a
                            className="c-advanced-filter-tutorial__inline-link"
                            href="https://nvdb-docs.atlas.vegvesen.no/nvdbapil/v4/introduksjon/Avanserte_filter"
                        >
                            avanserte filtere i API
                        </a>
                        .
                    </p>
                </section>

                <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--soft">
                    <p className="c-advanced-filter-tutorial__section-title">
                        Ugyldige filtere
                    </p>
                    <p>
                        Hvis et filter ikke er komplett, vises det en advarsel ved
                        filteret om hva som mangler for at det skal være gyldig.
                    </p>
                    <p>
                        Vegkart bruker i slike tilfeller det siste gyldige filteret i
                        API-kallet og viser en advarsel både i resultatet og i visningen
                        for generert filter.
                    </p>
                </section>
            </section>

            <section className="c-advanced-filter-tutorial__section">
                <SectionTitle>Bruk av Datakatalogen i Vegkart</SectionTitle>
                <p>
                    Datakatalogen gir oversikt over gyldige egenskapstyper,
                    egenskapsverdier og relasjoner mellom vegobjekter.
                </p>
                <p>
                    For å legge til <FilterTypeInline kind="egenskap" /> eller{' '}
                    <FilterTypeInline kind="relasjon" /> filter til søk direkte fra
                    Datakatalogen:
                </p>
                <ul className="c-advanced-filter-tutorial__list">
                    <li>
                        Trykk på <DatakatalogButtonPreview /> til høyre for søkefeltet i
                        Vegkart for å åpne den.
                    </li>
                    <li>Finn ønsket vegobjekttype og egenskapstype eller relasjon.</li>
                    <li>
                        Trykk <VisIVegkartPreview /> ved en egenskap eller relasjon for å
                        legge den til som filter.
                    </li>
                </ul>
            </section>

            <section className="c-advanced-filter-tutorial__section c-advanced-filter-tutorial__section--examples">
                <SectionTitle>Eksempler på søk du kan bygge</SectionTitle>
                <p>
                    Her er noen typiske brukerbehov og hvordan de kan se ut ferdig bygget
                    i filterbyggeren.
                </p>
                {tutorialExamples.length > 0 ? (
                    <div className="c-advanced-filter-tutorial__example-list">
                        {tutorialExamples.map((example, index) => (
                            <TutorialExampleCard
                                key={example.title}
                                example={example}
                                index={index}
                                allTypes={allTypes}
                            />
                        ))}
                    </div>
                ) : (
                    <p>
                        Eksempelkortene er ikke tilgjengelige for denne datakatalogen
                        akkurat nå.
                    </p>
                )}
            </section>
        </div>
    );
};
